import { useMsal } from '@azure/msal-react'
import { LineChart } from '@mantine/charts'
import {
  Alert,
  Group,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core'
import { useEffect, useState } from 'react'
import { fetchAdImpressions } from '../data/advertisement.service'
import { getScansDetailed, getScansSummary, QrScanDetailed, QrScanSummary } from '../data/qr.service'
import { AdImpressionsDto } from '../models/AdImpressionsDto'

interface AdMetricsPanelProps {
  advertisementId: number
  qrEnabled: boolean
}

interface DayPoint {
  date: string
  Impressions: number
  'QR Scans': number
}

interface LocationRow {
  locationName: string
  deviceName: string
  impressions: number
  scans: number
}

const StatCard = ({
  label,
  value,
  color,
}: {
  label: string
  value: number | string
  color?: string
}) => (
  <Paper withBorder p='md' radius='md'>
    <Text c='dimmed' size='sm' mb={4}>
      {label}
    </Text>
    <Title order={2} c={color}>
      {typeof value === 'number' ? Math.round(value).toLocaleString() : value}
    </Title>
  </Paper>
)

type ChartView = 'impressions' | 'scans'

export default function AdMetricsPanel({ advertisementId, qrEnabled }: AdMetricsPanelProps) {
  const { instance } = useMsal()
  const [impressions, setImpressions] = useState<AdImpressionsDto | null>(null)
  const [scanSummary, setScanSummary] = useState<QrScanSummary | null>(null)
  const [scanDetailed, setScanDetailed] = useState<QrScanDetailed | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [chartView, setChartView] = useState<ChartView>('impressions')

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    setError(null)

    const fetches: Promise<unknown>[] = [
      fetchAdImpressions(advertisementId, instance)
        .then((d) => { if (!cancelled) setImpressions(d) })
        .catch(() => { if (!cancelled) setError('Failed to load impression data.') }),
    ]

    if (qrEnabled) {
      fetches.push(
        Promise.all([
          getScansSummary(advertisementId, instance),
          getScansDetailed(advertisementId, instance),
        ]).then(([s, d]) => {
          if (!cancelled) { setScanSummary(s); setScanDetailed(d) }
        }).catch(() => {}),
      )
    }

    Promise.all(fetches).finally(() => {
      if (!cancelled) setIsLoading(false)
    })

    return () => { cancelled = true }
  }, [advertisementId, instance])

  if (isLoading) {
    return (
      <Stack gap='md'>
        <SimpleGrid cols={{ base: 2, sm: 4 }}>
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} h={80} radius='md' />
          ))}
        </SimpleGrid>
        <Skeleton h={260} radius='md' />
        <Skeleton h={160} radius='md' />
      </Stack>
    )
  }

  if (error) {
    return <Alert color='red' title='Error'>{error}</Alert>
  }

  // ── Build merged daily time-series ─────────────────────────────────────
  const dateMap = new Map<string, DayPoint>()

  impressions?.byDay.forEach((d) => {
    const key = new Date(d.date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })
    const existing = dateMap.get(key)
    dateMap.set(key, {
      date: key,
      Impressions: Math.round((existing?.Impressions ?? 0) + d.impressions),
      'QR Scans': existing?.['QR Scans'] ?? 0,
    })
  })

  scanDetailed?.series.forEach((p) => {
    const key = new Date(p.date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })
    const existing = dateMap.get(key)
    dateMap.set(key, {
      date: key,
      Impressions: existing?.Impressions ?? 0,
      'QR Scans': (existing?.['QR Scans'] ?? 0) + p.count,
    })
  })

  const seriesData: DayPoint[] = Array.from(dateMap.values()).sort((a, b) => {
    return new Date(a.date).getTime() - new Date(b.date).getTime()
  })

  // ── Build merged by-location table ─────────────────────────────────────
  const locationMap = new Map<string, LocationRow>()

  impressions?.byDay.forEach((day) => {
    day.byDevice.forEach((dev) => {
      const key = `${dev.locationName}\x00${dev.deviceName}`
      const existing = locationMap.get(key)
      locationMap.set(key, {
        locationName: dev.locationName,
        deviceName: dev.deviceName,
        impressions: Math.round((existing?.impressions ?? 0) + dev.impressions),
        scans: existing?.scans ?? 0,
      })
    })
  })

  scanDetailed?.byLocation.forEach((loc) => {
    const key = `${loc.locationName ?? ''}\x00${loc.deviceName ?? ''}`
    const existing = locationMap.get(key)
    locationMap.set(key, {
      locationName: loc.locationName ?? '—',
      deviceName: loc.deviceName ?? '—',
      impressions: existing?.impressions ?? 0,
      scans: (existing?.scans ?? 0) + loc.count,
    })
  })

  const locationRows = Array.from(locationMap.values()).sort(
    (a, b) => b.impressions - a.impressions,
  )

  const hasImpressions = (impressions?.totalImpressions ?? 0) > 0
  const hasScans = (scanSummary?.total ?? 0) > 0
  const hasAnyData = hasImpressions || hasScans

  const isImpressionsView = chartView === 'impressions'
  const activeChartSeries = isImpressionsView
    ? [{ name: 'Impressions' as const, color: 'blue.5' }]
    : [{ name: 'QR Scans' as const, color: 'teal.5' }]
  const activeChartColor = isImpressionsView
    ? 'var(--mantine-color-blue-5)'
    : 'var(--mantine-color-teal-5)'

  return (
    <Stack gap='lg'>
      <Title order={3}>Performance</Title>

      {/* ── Stat cards ─────────────────────────────────────────────── */}
      <SimpleGrid cols={{ base: 2, sm: qrEnabled ? 4 : 2 }}>
        <StatCard
          label='Total impressions'
          value={impressions?.totalImpressions ?? 0}
          color='blue.7'
        />
        <StatCard
          label='Last 30 days'
          value={
            impressions?.byDay
              .slice(-30)
              .reduce((s, d) => s + d.impressions, 0) ?? 0
          }
          color='blue.5'
        />
        {qrEnabled && (
          <>
            <StatCard
              label='QR scans (total)'
              value={scanSummary?.total ?? 0}
              color='teal.7'
            />
            <StatCard
              label='QR scans (7 days)'
              value={scanSummary?.last7Days ?? 0}
              color='teal.5'
            />
          </>
        )}
      </SimpleGrid>

      {/* ── Combined time-series chart ─────────────────────────────── */}
      <Paper withBorder p='md' radius='md'>
        <Group justify='space-between' mb='md' align='center'>
          <Group gap={8} align='center'>
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: activeChartColor,
                flexShrink: 0,
              }}
            />
            <Title order={4}>
              {isImpressionsView ? 'Impressions' : 'QR Scans'} over time
            </Title>
          </Group>
          {qrEnabled && hasScans && (
            <SegmentedControl
              size='xs'
              value={chartView}
              onChange={(v) => setChartView(v as ChartView)}
              data={[
                { label: 'Impressions', value: 'impressions' },
                { label: 'QR Scans', value: 'scans' },
              ]}
            />
          )}
        </Group>

        {seriesData.length === 0 ? (
          <Text c='dimmed' py='xl' ta='center'>
            No data yet for this period.
          </Text>
        ) : (
          <LineChart
            h={260}
            data={seriesData}
            dataKey='date'
            series={activeChartSeries}
            curveType='monotone'
            withDots={seriesData.length <= 60}
            yAxisLabel={isImpressionsView ? 'Impressions' : 'QR Scans'}
            gridAxis='xy'
          />
        )}
      </Paper>

      {/* ── By-location breakdown ──────────────────────────────────── */}
      {locationRows.length > 0 && (
        <Paper withBorder p='md' radius='md'>
          <Title order={4} mb='sm'>
            By location
          </Title>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Location</Table.Th>
                <Table.Th>Device</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Impressions</Table.Th>
                {qrEnabled && (
                  <Table.Th style={{ textAlign: 'right' }}>QR Scans</Table.Th>
                )}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {locationRows.map((row, i) => (
                <Table.Tr key={i}>
                  <Table.Td>{row.locationName}</Table.Td>
                  <Table.Td>{row.deviceName}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    {row.impressions.toLocaleString()}
                  </Table.Td>
                  {qrEnabled && (
                    <Table.Td style={{ textAlign: 'right' }}>
                      {row.scans.toLocaleString()}
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {!hasAnyData && !isLoading && (
        <Text c='dimmed' ta='center' py='xl'>
          No performance data available yet. Check back once your ad starts running.
        </Text>
      )}
    </Stack>
  )
}
